import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../App.css";
import axios from "axios";
import { useProject } from "../ProjectContext";
import Sidebar from "../componenteExtra/SidebarSolicitante";
import Modal from "react-modal";
import { Toaster, toast } from "react-hot-toast";
import SessionManager from '../componenteExtra/SessionManager';

const IngresarMultiplesSolicitudes = () => {
  const navigate = useNavigate();
  const { projectData } = useProject();
  const [projectActivities, setProjectActivities] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [procedimiento, setProcedimiento] = useState("");
  const token = localStorage.getItem("access_token");

  // Ref para el checkbox global
  const globalCheckboxRef = useRef(null);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const allProjects = [
          {
            codigo_proyecto: projectData.projectCode,
            nombre_proyecto: projectData.projectName,
            razon_social: projectData.businessName,
            filial: projectData.businessName,
            rut_filial: projectData.rutFilial,
            socio_responsable: `${projectData.partnerInCharge1} ${projectData.partnerInCharge2}`,
            tipo_solicitud: projectData.selectedType,
            tipo_regulacion: projectData.regulacion,
            fecha_emision: projectData.selectedDate || "",
          },
          ...projectData.selectedProjects.map((proj) => ({
            ...proj,
            filial: proj.businessName || proj.razon_social,
            tipo_solicitud: proj.selectedType || "No especificado",
            fecha_emision: proj.emissionDate || "",
          })),
          ...(projectData.multipleFilials || []).map((filial) => ({
            codigo_proyecto: projectData.projectCode,
            nombre_proyecto: projectData.projectName,
            razon_social: projectData.businessName,
            filial: filial.filial,
            rut_filial: filial.rut,
            socio_responsable: `${projectData.partnerInCharge1} ${projectData.partnerInCharge2}`,
            tipo_solicitud: projectData.selectedType,
            tipo_regulacion: projectData.regulacion,
            fecha_emision: projectData.selectedDate || "",
          })),
        ];

        const groupedActivities = [];

        for (const project of allProjects) {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/activities`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              params: { project_code: project.codigo_proyecto },
            }
          );
          groupedActivities.push({
            ...project,
            activities: response.data.map((activity) => ({
              ...activity,
              fechaEntregaInformacion: "",
              fechaTerminoActividad: "",
              selected: false,
            })),
          });
        }

        setProjectActivities(groupedActivities);
      } catch (error) {
        console.error("Error fetching activities:", error);
        toast.error("Error al cargar actividades");
      }
    };

    fetchActivities();
  }, [projectData, token]);

  // === TOGGLE INDIVIDUAL ===
  const toggleRow = (projectCode, filial, id) => {
    setProjectActivities((prev) =>
      prev.map((project) =>
        project.codigo_proyecto === projectCode && project.filial === filial
          ? {
              ...project,
              activities: project.activities.map((act) =>
                act.id === id ? { ...act, selected: !act.selected } : act
              ),
            }
          : project
      )
    );
  };

  // === TOGGLE TODAS DE UNA FILIAL ===
  const toggleProjectAll = (projectCode, filial, selectAll) => {
    setProjectActivities((prev) =>
      prev.map((project) =>
        project.codigo_proyecto === projectCode && project.filial === filial
          ? {
              ...project,
              activities: project.activities.map((act) => ({
                ...act,
                selected: selectAll,
              })),
            }
          : project
      )
    );
  };

  // === TOGGLE TODAS GLOBAL ===
  const toggleGlobalAll = () => {
    const allCurrentlySelected = projectActivities.every((proj) =>
      proj.activities.every((act) => act.selected)
    );
    const newState = !allCurrentlySelected;

    setProjectActivities((prev) =>
      prev.map((proj) => ({
        ...proj,
        activities: proj.activities.map((act) => ({
          ...act,
          selected: newState,
        })),
      }))
    );
  };

  // === CALCULAR ESTADO GLOBAL ===
  const getGlobalCheckboxState = () => {
    const totalActivities = projectActivities.reduce(
      (sum, proj) => sum + proj.activities.length,
      0
    );
    const selectedActivities = projectActivities.reduce(
      (sum, proj) => sum + proj.activities.filter((a) => a.selected).length,
      0
    );

    if (selectedActivities === 0) return { checked: false, indeterminate: false };
    if (selectedActivities === totalActivities) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  const globalState = getGlobalCheckboxState();

  // === FECHAS ===
  const handleDateChange = (projectCode, filial, id, date) => {
    const selectedDate = new Date(date);
    const currentDate = new Date();
    if (selectedDate < currentDate) {
      toast.error("Fecha posterior a la actual");
      return;
    }
    if ([0, 6].includes(selectedDate.getUTCDay())) {
      toast.error("No se permiten fines de semana");
      return;
    }

    const project = projectActivities.find((p) => p.codigo_proyecto === projectCode && p.filial === filial);
    const actividad = project?.activities.find((a) => a.id === id);
    if (!actividad) return;

    const maxEntregaDate = new Date(actividad.fechaTerminoActividad);
    maxEntregaDate.setDate(maxEntregaDate.getDate() - 5);
    if (selectedDate > maxEntregaDate) {
      toast.error("Debe ser al menos 5 días antes del término");
      return;
    }

    setProjectActivities((prev) =>
      prev.map((project) =>
        project.codigo_proyecto === projectCode && project.filial === filial
          ? {
              ...project,
              activities: project.activities.map((act) =>
                act.selected && !act.fechaEntregaInformacion
                  ? { ...act, fechaEntregaInformacion: date }
                  : act.id === id
                  ? { ...act, fechaEntregaInformacion: date }
                  : act
              ),
            }
          : project
      )
    );
  };

  const handleEndDateChange = (projectCode, filial, id, date) => {
    const selectedDate = new Date(date);
    const currentDate = new Date();
    if (selectedDate < currentDate) {
      toast.error("Fecha posterior a la actual");
      return;
    }
    if ([0, 6].includes(selectedDate.getUTCDay())) {
      toast.error("No se permiten fines de semana");
      return;
    }

    const project = projectActivities.find((p) => p.codigo_proyecto === projectCode && p.filial === filial);
    const actividad = project?.activities.find((a) => a.id === id);
    if (!actividad) return;

    const minEndDate = new Date(actividad.fechaEntregaInformacion);
    minEndDate.setDate(minEndDate.getDate() + 5);
    const maxEndDate = new Date(project.fecha_emision);
    maxEndDate.setDate(maxEndDate.getDate() - 3);

    if (selectedDate < minEndDate || selectedDate > maxEndDate) {
      toast.error("Fecha de término fuera de rango permitido");
      return;
    }

    setProjectActivities((prev) =>
      prev.map((project) =>
        project.codigo_proyecto === projectCode && project.filial === filial
          ? {
              ...project,
              activities: project.activities.map((act) =>
                act.selected && !act.fechaTerminoActividad
                  ? { ...act, fechaTerminoActividad: date }
                  : act.id === id
                  ? { ...act, fechaTerminoActividad: date }
                  : act
              ),
            }
          : project
      )
    );
  };

  const handleContinue = async () => {
    const currentDate = new Date().toISOString().split("T")[0];
    const nombreSolicitante = localStorage.getItem("nombre_get");
    const selectedActivities = [];

    projectActivities.forEach((project) => {
      let fechaEmision = project.fecha_emision || null;
      if (
        project.tipo_solicitud === "Trabajo final" &&
        (project.tipo_regulacion === "CMF" || project.tipo_regulacion === "Otros")
      ) {
        if (!fechaEmision) {
          toast.error(`Falta fecha de emisión en ${project.codigo_proyecto}`);
          return;
        }
        fechaEmision = new Date(fechaEmision).toISOString().slice(0, 10);
      }

      project.activities.forEach((activity) => {
        if (activity.selected) {
          selectedActivities.push({
            codigo_proyecto: project.codigo_proyecto,
            nombre_proyecto: project.nombre_proyecto,
            razon_social: projectData.holding,
            tipo_regulacion: projectData.regulacion,
            tipo_solicitud: project.tipo_solicitud,
            estado_asignaciones: 1,
            filial: project.filial,
            encargado_auditoria: project.socio_responsable,
            rubro_get: activity.rubro_get,
            nombre_actividad_get: activity.nombre_actividad_get,
            numero_grupo: activity.numero_grupo,
            jefe_grupo: activity.jefe_grupo,
            subrogante: activity.subrogante,
            fecha_entrega_informacion: activity.fechaEntregaInformacion
              ? new Date(activity.fechaEntregaInformacion).toISOString().slice(0, 10)
              : null,
            fecha_termino_senior_actividad: activity.fechaTerminoActividad
              ? new Date(activity.fechaTerminoActividad).toISOString().slice(0, 10)
              : null,
            staff_asignado: null,
            fecha_de_inicio: null,
            fecha_de_termino: null,
            fecha_estado_staff: null,
            fecha_inscripción_actividad: currentDate,
            horas_get: null,
            nombre_solicitante: nombreSolicitante,
            fecha_corte_auditoria: projectData.cutOffDate,
            enlace_comentario: projectData.enlace,
            fecha_emision: fechaEmision
          });
        }
      });
    });

    if (selectedActivities.length === 0) {
      toast.error("Seleccione al menos una actividad.");
      return;
    }

    const allDatesFilled = selectedActivities.every(
      (a) => a.fecha_entrega_informacion && a.fecha_termino_senior_actividad
    );
    if (!allDatesFilled) {
      toast.error("Complete todas las fechas.");
      return;
    }

    const toastId = toast.loading("Guardando...");
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/test_asignacion_get`,
        selectedActivities,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.dismiss(toastId);
      toast.success("Guardado correctamente");
      setTimeout(() => navigate("/menusolicitante"), 3000);
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Error: " + error.message);
    }
  };

  const openModal = (proc) => {
    setProcedimiento(proc);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setProcedimiento("");
  };

  return (
    <div className="app">
      <SessionManager />
      <Sidebar />
      <div className="main-content">
        <div className="main-panel">
          <h1>Actividades GET</h1>

          {/* === CHECKBOX GLOBAL === */}
          <div className="global-select-all" style={{ marginBottom: "20px", fontWeight: "bold" }}>
            <label>
              <input
                type="checkbox"
                ref={globalCheckboxRef}
                checked={globalState.checked}
                onChange={toggleGlobalAll}
                style={{ marginRight: "8px" }}
              />
              Marcar todas las todas las filiales y actividades
            </label>
          </div>

          {/* === BLOQUES DE FILIALES === */}
          {projectActivities.map((project) => (
            <ProjectCapsule
              key={`${project.codigo_proyecto}-${project.filial}`}
              project={project}
              onToggleSelectActivity={toggleRow}
              onToggleProjectAll={toggleProjectAll}
              handleDateChange={handleDateChange}
              handleEndDateChange={handleEndDateChange}
              openModal={openModal}
            />
          ))}

          <button className="continue-button" onClick={handleContinue}>
            Continuar
          </button>
        </div>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        className="modal-content"
        overlayClassName="modal-overlay"
      >
        <h2>Procedimiento</h2>
        <p>{procedimiento}</p>
        <button onClick={closeModal} className="close-button">Cerrar</button>
      </Modal>
      <Toaster />

      {/* === EFECTO PARA INDETERMINADO GLOBAL === */}
      <script
        dangerouslySetInnerHTML={{
          __html: `
            if (window.globalCheckboxRef) {
              window.globalCheckboxRef.indeterminate = ${globalState.indeterminate};
            }
          `,
        }}
      />
    </div>
  );
};

// === COMPONENTE PROJECTCAPSULE CON CHECKBOX EN ENCABEZADO ===
const ProjectCapsule = ({
  project,
  onToggleSelectActivity,
  onToggleProjectAll,
  handleDateChange,
  handleEndDateChange,
  openModal,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const headerCheckboxRef = useRef(null);

  const allSelected = project.activities.every((a) => a.selected);
  const someSelected = project.activities.some((a) => a.selected) && !allSelected;

  const toggleExpand = () => setIsExpanded(!isExpanded);
  const handleHeaderCheckbox = () => {
    onToggleProjectAll(project.codigo_proyecto, project.filial, !allSelected);
  };

  // Actualizar estado indeterminado
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  return (
    <div className="project-capsule" style={{ marginBottom: "20px", border: "1px solid #ddd", borderRadius: "8px" }}>
      {/* === ENCABEZADO CON CHECKBOX === */}
      <div
        className="project-header"
        onClick={toggleExpand}
        style={{
          padding: "12px 16px",
          backgroundColor: "#f7f7f7",
          borderBottom: isExpanded ? "1px solid #ddd" : "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
          <input
            type="checkbox"
            ref={headerCheckboxRef}
            checked={allSelected}
            onChange={handleHeaderCheckbox}
            onClick={(e) => e.stopPropagation()} // Evita expandir al hacer click
            style={{ marginRight: "12px" }}
          />
          <h2 style={{ margin: 0, fontSize: "16px", flex: 1 }}>
            {`${project.codigo_proyecto} - ${project.nombre_proyecto} - ${project.filial} - ${project.socio_responsable} - ${project.tipo_solicitud}`}
            {project.fecha_emision && ` - ${new Date(project.fecha_emision).toLocaleDateString("es-ES")}`}
          </h2>
        </div>
        <span style={{ fontSize: "20px" }}>{isExpanded ? "▲" : "▼"}</span>
      </div>

      {/* === TABLA (solo si está expandido) === */}
      {isExpanded && (
        <table className="activities-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th>Seleccionar</th>
              <th>Grupo</th>
              <th>Rubro</th>
              <th>Actividad</th>
              <th>Código GET</th>
              <th>Procedimiento</th>
              <th>Fecha de entrega de información</th>
              <th>Fecha de término</th>
              <th>Jefe de grupo</th>
              <th>Subrogante</th>
            </tr>
          </thead>
          <tbody>
            {project.activities.map((actividad) => (
              <tr key={actividad.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={actividad.selected}
                    onChange={() =>
                      onToggleSelectActivity(project.codigo_proyecto, project.filial, actividad.id)
                    }
                  />
                </td>
                <td>{actividad.numero_grupo}</td>
                <td>{actividad.rubro_get}</td>
                <td>{actividad.nombre_actividad_get}</td>
                <td>{actividad.codigo_actividad_get}</td>
                <td>
                  <button onClick={() => openModal(actividad.procedimiento)}>Ver</button>
                </td>
                <td>
                  <input
                    type="date"
                    value={actividad.fechaEntregaInformacion || ""}
                    onChange={(e) => {
                      handleDateChange(project.codigo_proyecto, project.filial, actividad.id, e.target.value);
                      e.target.classList.toggle("highlight", e.target.value !== "");
                    }}
                    disabled={!actividad.selected}
                    className={actividad.fechaEntregaInformacion ? "highlight" : ""}
                  />
                </td>
                <td>
                  <input
                    type="date"
                    value={actividad.fechaTerminoActividad || ""}
                    onChange={(e) => {
                      handleEndDateChange(project.codigo_proyecto, project.filial, actividad.id, e.target.value);
                      e.target.classList.toggle("highlight", e.target.value !== "");
                    }}
                    disabled={!actividad.selected}
                    className={actividad.fechaTerminoActividad ? "highlight" : ""}
                  />
                </td>
                <td>{actividad.jefe_grupo}</td>
                <td>{actividad.subrogante}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default IngresarMultiplesSolicitudes;